import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { NavifyService } from '../services/navify.service';
import { OAuth2Service } from '../services/oauth2.service';

/**
 * Standalone callback component. Receives the OAuth2 authorization code from the
 * provider redirect, validates the state, and proxies the code to the backend BFF.
 *
 * @author oauth2-bff
 */
@Component({
  standalone: true,
  selector: 'app-oauth2-callback',
  templateUrl: './oauth2-callback.component.html',
  styleUrls: ['./oauth2-callback.component.scss'],
  imports: [CommonModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class Oauth2CallbackComponent implements OnInit, OnDestroy {

  isLoading = true;
  hasError = false;

  private callbackSubscription: Subscription | null = null;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly oauth2Service: OAuth2Service,
    private readonly navifyService: NavifyService
  ) {}

  ngOnInit(): void {
    this.handleCallback();
  }

  ngOnDestroy(): void {
    this.callbackSubscription?.unsubscribe();
  }

  private handleCallback(): void {
    const params = this.route.snapshot.queryParamMap;
    const error = params.get('error');
    const code = params.get('code');
    const state = params.get('state');

    console.info('[OAuth2Callback] Full callback URL:', window.location.href);
    console.info('[OAuth2Callback] Query string:', window.location.search);
    console.info('[OAuth2Callback] Extracted params:', { code: code || 'MISSING', state: state || 'MISSING', error: error || 'NONE' });

    if (error) {
      console.error('[OAuth2Callback] OAuth2 provider returned error:', error);
      void this.handleError(`OAuth2 provider returned error: ${error}`);
      return;
    }

    if (!code || !state) {
      console.error('[OAuth2Callback] Missing code or state parameter in callback');
      void this.handleError('Missing code or state parameter in callback');
      return;
    }

    console.info('[OAuth2Callback] Code and state present, proceeding with token exchange');
    this.callbackSubscription = this.oauth2Service.handleCallback(code, state).subscribe({
      next: (success: boolean) => {
        if (success) {
          console.info('[OAuth2Callback] Token exchange successful');
          const returnUrl = sessionStorage.getItem('oauth2_return_url') ?? '/';
          sessionStorage.removeItem('oauth2_return_url');
          sessionStorage.removeItem('oauth2_flow_in_progress');
          sessionStorage.removeItem('oauth2_auto_triggered');
          
          this.isLoading = false;
          void this.router.navigate([returnUrl], { replaceUrl: true });
        } else {
          console.error('[OAuth2Callback] Backend code exchange returned failure response');
          void this.handleError('Backend code exchange returned failure response');
        }
      },
      error: (error: unknown) => {
        console.error('[OAuth2Callback] Unexpected callback error:', error);
        void this.handleError(`Unexpected callback error: ${String(error)}`);
      }
    });
  }

  async handleError(reason: string): Promise<void> {
    console.info('[OAuth2Callback] Error during authorization callback:', reason);
    this.isLoading = false;
    this.hasError = true;

    this.oauth2Service.markExchangeFailed();
    sessionStorage.removeItem('oauth2_flow_in_progress');
    sessionStorage.removeItem('oauth2_auto_triggered');

    try {
      await this.navifyService.logOut();
    } catch (error) {
      console.info('[OAuth2Callback] logOut threw during error handling:', error);
    }
    void this.router.navigate(['/login'], { replaceUrl: true });
  }
}
